/**
 * The facts a GA4 `purchase` event is built from, taken ONLY from a Stripe Checkout session the server has
 * retrieved itself. The browser never supplies a price: before this, the page used a hard-coded price table and
 * fired on any "?checkout=success&plan=..." URL once the visitor had Pro access, so a test payment, a coupon, a
 * Premium purchase or a reload could all produce a wrong or missing purchase.
 *
 * Nothing personal is read or returned: no email, name, card or customer id.
 */
import { productTagFromMetadata, type ProductTag } from "./funnel";

/** Stripe Checkout session ids look like cs_live_... or cs_test_.... Anything else is rejected before Stripe is called. */
export const CHECKOUT_SESSION_ID_RE = /^cs_(live|test)_[A-Za-z0-9]{10,200}$/;

export type Tier = "professional" | "premium";
export type CheckoutPlan = "day_pass" | "monthly" | "api";

/** What each plan sells at, in USD, for the `checkout_start` event only. Purchases always use Stripe's own amount. */
export const CHECKOUT_PLAN_VALUES: Record<Tier, Record<"day_pass" | "monthly", number>> = {
  professional: { day_pass: 1, monthly: 4.99 },
  premium: { day_pass: 3, monthly: 9.99 },
};

export interface SessionFacts {
  id: string;
  livemode: boolean;
  mode?: string | null;
  payment_status?: string | null;
  amount_total?: number | null;
  currency?: string | null;
  metadata?: Record<string, string | null | undefined> | null;
}

export type PurchaseSkip = "test_mode" | "not_paid" | "no_value";

export interface PurchaseDetails {
  /** True only for a real (live-mode), paid session with money attached. The page fires `purchase` only then. */
  countable: boolean;
  skip?: PurchaseSkip;
  /** The Checkout session id: stable per payment, so GA4 drops a repeated purchase with the same transaction_id. */
  transactionId: string;
  value: number;
  currency: string;
  tier: "professional" | "premium";
  /** day_pass, monthly, or the product tag for anything that is not a Day Pass / monthly plan (api, workflow, ...). */
  plan: string;
  product: ProductTag;
}

export function purchaseFromSession(s: SessionFacts): PurchaseDetails {
  const product = productTagFromMetadata(s.metadata);
  const tier = product === "premium_apps" ? "premium" : "professional";
  const metaPlan = s.metadata?.plan;
  const plan = metaPlan === "day_pass" || metaPlan === "monthly" ? metaPlan : product === "pro" ? (s.mode === "subscription" ? "monthly" : "day_pass") : product;
  const value = typeof s.amount_total === "number" && s.amount_total > 0 ? Math.round(s.amount_total) / 100 : 0;
  const base = { transactionId: s.id, value, currency: (s.currency || "usd").toUpperCase(), tier, plan, product } as const;
  if (!s.livemode) return { ...base, countable: false, skip: "test_mode" };
  if (s.payment_status !== "paid") return { ...base, countable: false, skip: "not_paid" };
  if (value <= 0) return { ...base, countable: false, skip: "no_value" };
  return { ...base, countable: true };
}
